import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageCard } from '../layout/page/PageCard';
import { DeleteIconButton } from '../ui/DeleteIconButton';
import { InlineAlert } from '../ui/InlineAlert';
import { RoundedCheckbox } from '../ui/RoundedCheckbox';
import { usePermissions } from '../../contexts/PermissionsContext';
import { canWriteProductCatalog } from './productCatalogAccess';
import {
  SalesProductCapabilityLink,
  SalesProductCapabilityLinkItemPayload,
  getProductCapabilities,
  listSalesCapabilities,
  replaceProductCapabilities,
} from '../../services/salesCapabilitiesApi';
import { CatalogMultiSelectModal, CatalogSelectItem } from './CatalogMultiSelectModal';

type ProductCapabilitiesPanelProps = {
  productId: string;
  readOnly?: boolean;
};

type DraftLink = SalesProductCapabilityLinkItemPayload & {
  name: string;
};

export function ProductCapabilitiesPanel({ productId, readOnly = false }: ProductCapabilitiesPanelProps) {
  const { canWriteModule } = usePermissions();
  const canWrite = !readOnly && canWriteProductCatalog(canWriteModule);
  const [links, setLinks] = useState<DraftLink[]>([]);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(() => new Set());
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const linkedIds = useMemo(() => links.map((link) => link.capabilityId), [links]);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        const productLinks = await getProductCapabilities(productId);
        setLinks(mapLinksToDraft(productLinks));
        setCheckedIds(new Set());
      } catch {
        toast.error('Falha ao carregar capabilities do produto.');
      } finally {
        setLoading(false);
      }
    })();
  }, [productId]);

  function mapLinksToDraft(productLinks: SalesProductCapabilityLink[]): DraftLink[] {
    return productLinks.map((link) => ({
      capabilityId: link.capabilityId,
      name: link.name,
      isIncluded: link.isIncluded,
      quantityDefault: link.quantityDefault ?? undefined,
      sortOrder: link.sortOrder,
    }));
  }

  function updateLink(
    capabilityId: string,
    patch: Partial<Pick<DraftLink, 'isIncluded' | 'quantityDefault'>>,
  ): void {
    if (!canWrite) return;
    setLinks((current) =>
      current.map((link) => (link.capabilityId === capabilityId ? { ...link, ...patch } : link)),
    );
  }

  function toggleChecked(capabilityId: string): void {
    setCheckedIds((current) => {
      const next = new Set(current);
      if (next.has(capabilityId)) {
        next.delete(capabilityId);
      } else {
        next.add(capabilityId);
      }
      return next;
    });
  }

  function removeOne(capabilityId: string): void {
    if (!canWrite) return;
    setLinks((current) => current.filter((link) => link.capabilityId !== capabilityId));
    setCheckedIds((current) => {
      const next = new Set(current);
      next.delete(capabilityId);
      return next;
    });
  }

  function removeSelected(): void {
    if (!canWrite || checkedIds.size === 0) return;
    setLinks((current) => current.filter((link) => !checkedIds.has(link.capabilityId)));
    setCheckedIds(new Set());
  }

  function handleModalConfirm(selected: CatalogSelectItem[]): void {
    if (!canWrite || selected.length === 0) return;

    setLinks((current) => {
      const existing = new Set(current.map((link) => link.capabilityId));
      const additions = selected
        .filter((item) => !existing.has(item.id))
        .map((item, index) => ({
          capabilityId: item.id,
          name: item.title,
          isIncluded: true,
          quantityDefault: undefined,
          sortOrder: current.length + index,
        }));
      return [...current, ...additions];
    });
  }

  async function handleSave(): Promise<void> {
    if (!canWrite) return;

    setSaving(true);
    try {
      const items: SalesProductCapabilityLinkItemPayload[] = links.map((link, index) => ({
        capabilityId: link.capabilityId,
        isIncluded: link.isIncluded ?? true,
        quantityDefault: link.quantityDefault ?? null,
        sortOrder: index,
      }));
      await replaceProductCapabilities(productId, items);
      toast.success('Capabilities do produto atualizadas.');
    } catch {
      toast.error('Falha ao salvar capabilities do produto.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <p className="text-secondary-500">Carregando capabilities...</p>;
  }

  return (
    <PageCard
      title="Capabilities"
      subtitle="Vincule funcionalidades do catálogo e complete os detalhes da oferta."
    >
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-secondary-600">
          Catálogo em{' '}
          <Link to="/sales/capabilities" className="erp-link">
            Capabilities de produto
          </Link>
          .
        </p>
        {canWrite ? (
          <div className="erp-btn-actions">
            <button
              type="button"
              className="erp-btn-danger erp-btn-sm"
              disabled={checkedIds.size === 0}
              onClick={removeSelected}
            >
              Excluir selecionados
            </button>
            <button type="button" className="erp-btn-secondary" onClick={() => setModalOpen(true)}>
              Adicionar capabilities
            </button>
          </div>
        ) : null}
      </div>

      {links.length === 0 ? (
        <InlineAlert>Nenhuma capability vinculada. Use “Adicionar capabilities”.</InlineAlert>
      ) : (
        <ul className="space-y-2">
          {links.map((link) => (
            <li key={link.capabilityId} className="rounded-lg border border-secondary-200 px-3 py-2">
              <div className="flex items-start gap-3">
                {canWrite ? (
                  <RoundedCheckbox
                    className="mt-1"
                    checked={checkedIds.has(link.capabilityId)}
                    onChange={() => toggleChecked(link.capabilityId)}
                  />
                ) : null}
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <span className="font-medium text-secondary-900">{link.name}</span>
                    </div>
                    {canWrite ? (
                      <DeleteIconButton
                        label="Excluir"
                        onClick={() => removeOne(link.capabilityId)}
                      />
                    ) : null}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-4 text-sm">
                    <label className="erp-label-inline">
                      <input
                        type="checkbox"
                        checked={link.isIncluded ?? true}
                        disabled={!canWrite}
                        onChange={(event) =>
                          updateLink(link.capabilityId, { isIncluded: event.target.checked })
                        }
                      />
                      Incluída no pacote
                    </label>
                    <label className="erp-label-inline">
                      Qtd. padrão
                      <input
                        type="number"
                        min={0}
                        className="erp-input ml-2 w-24"
                        disabled={!canWrite}
                        value={link.quantityDefault ?? ''}
                        onChange={(event) =>
                          updateLink(link.capabilityId, {
                            quantityDefault: event.target.value
                              ? Number(event.target.value)
                              : undefined,
                          })
                        }
                      />
                    </label>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {canWrite ? (
        <div className="mt-4">
          <button
            type="button"
            className="erp-btn-primary"
            disabled={saving}
            onClick={() => void handleSave()}
          >
            {saving ? 'Salvando...' : 'Salvar capabilities'}
          </button>
        </div>
      ) : null}

      <CatalogMultiSelectModal
        open={modalOpen}
        title="Selecionar capabilities"
        subtitle="Role para carregar mais itens do catálogo."
        alreadySelectedIds={linkedIds}
        fetchPage={async (page, pageSize, search) => {
          const result = await listSalesCapabilities({ activeOnly: true, page, pageSize, search });
          return {
            items: result.items.map((item) => ({
              id: item.id,
              title: item.name,
              subtitle: item.category,
              description: item.description,
            })),
            totalCount: result.totalCount,
            page: result.page,
            pageSize: result.pageSize,
          };
        }}
        onClose={() => setModalOpen(false)}
        onConfirm={handleModalConfirm}
      />
    </PageCard>
  );
}
